import GoalCollapse from './GoalCollapse';
import React, { useEffect, useRef, useState } from 'react';
import LoadingIndicator from '../shared/LoadingIndicator';
import { Button, Checkbox, Drawer, FormGroup, Icon, Slider, Tooltip } from '@blueprintjs/core';
import ConfirmationDialog from '../../views/ConfirmationDialog';
import axios from 'axios';
import Cookies from 'js-cookie';
import ToastStore from '../../stores/ToastStore';
import FilledButton from '../buttons-20250224T111359Z-001/buttons/FilledButton.jsx';
import TextButton from '../buttons-20250224T111359Z-001/buttons/TextButton.jsx';
import GoalProgressRow from './GoalProgressRow';
import Progress from '../../assets/Progress.svg';
import EditIcon from '../../assets/editIcon.svg';
import DeleteIcon from '../../assets/Delete.svg';
import './MilestoneList.css';

export default function MilestoneList({ subGoal, goalSettingId, userId, editable, onRefresh }) {
  const [showMilestones, setShowMilestones] = useState(false);
  const [openTasks, setOpenTasks] = useState({});
  const [pendingAction, setPendingAction] = useState(null);
  const [formData, setFormData] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const isMounted = useRef(true);
  const isRequestPending = useRef(false);
  const isLoading = isSaving || isDeleting || !!pendingAction;
  const milestones = Array.isArray(subGoal.milestones) ? subGoal.milestones : [];
  const milestonesOpen = milestones.length > 0 && showMilestones;

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const getTasks = milestone => Array.isArray(milestone.tasks) ? milestone.tasks : [];
  const canEditMilestoneProgress = formData && formData.type === 'milestone' &&
    milestones.some(milestone => milestone.id === formData.id && getTasks(milestone).length === 0);
  const isCompleted = task => task.status != null ? Number(task.status) === 1 : task.completed === true;

  const openMilestoneDrawer = milestone => {
    if (!editable || isRequestPending.current) return;
    setErrorMessage('');
    setFormData({
      type: 'milestone', id: milestone.id, title: milestone.title || '',
      progress: Math.min(100, Math.max(0, Number(milestone.progress) || 0))
    });
  };

  const openTaskDrawer = (milestone, task) => {
    if (!editable || isRequestPending.current) return;
    setErrorMessage('');
    setFormData({
      type: 'task', milestoneId: milestone.id, id: task.id,
      title: task.title || '', status: isCompleted(task) ? 1 : 0
    });
  };

  const closeDrawer = () => {
    if (isRequestPending.current) return;
    setFormData(null);
    setErrorMessage('');
  };

  const closeDeleteDialog = () => {
    if (isRequestPending.current) return;
    setItemToDelete(null);
    setErrorMessage('');
  };

  const getMilestoneParams = () => {
    if (!subGoal.id || !goalSettingId || !userId) {
      throw new Error('Missing sub-goal, goal cycle or employee for this milestone.');
    }
    return { sub_goal_id: subGoal.id, goal_setting_id: goalSettingId, user_id: userId };
  };

  const refreshMilestones = async () => {
    if (!isMounted.current) return;
    try {
      const result = await onRefresh();
      if (!result) throw new Error('Refresh failed');
    } catch (error) {
      if (isMounted.current) ToastStore.showToast('Change saved. Reload the page to see the latest data.', 'warning');
    }
  };

  const handleSaveMilestone = async () => {
    const payload = { ...getMilestoneParams(), title: formData.title.trim() };
    if (canEditMilestoneProgress) payload.progress = formData.progress;
    const response = await axios.put(`${Cookies.get('apiEnv')}/milestone/${formData.id}`, payload);
    if (!isMounted.current) return;
    ToastStore.showToast(response.data.message, 'success');
    await refreshMilestones();
  };

  const handleSaveTask = async () => {
    const response = await axios.put(`${Cookies.get('apiEnv')}/task/${formData.id}`, {
      ...getMilestoneParams(), milestone_id: formData.milestoneId,
      title: formData.title.trim(), status: formData.status
    });
    if (!isMounted.current) return;
    ToastStore.showToast(response.data.message, 'success');
    await refreshMilestones();
  };

  const handleCreate = async (milestone = null) => {
    if (!editable || isRequestPending.current) return;
    isRequestPending.current = true;
    setPendingAction(milestone ? `create-task-${milestone.id}` : 'create-milestone');
    setErrorMessage('');
    try {
      const payload = getMilestoneParams();
      if (milestone) payload.milestone_id = milestone.id;
      const response = await axios.post(`${Cookies.get('apiEnv')}/${milestone ? 'task' : 'milestone'}`, payload);
      if (!isMounted.current) return;
      ToastStore.showToast(response.data.message, 'success');
      setShowMilestones(true);
      if (milestone) setOpenTasks(previous => ({ ...previous, [milestone.id]: true }));
      await refreshMilestones();
    } catch (error) {
      if (isMounted.current) setErrorMessage(error.response?.data?.error || error.response?.data?.message || error.message || 'Unable to create. Please try again.');
    } finally {
      isRequestPending.current = false;
      if (isMounted.current) setPendingAction(null);
    }
  };

  const handleSave = async event => {
    event.preventDefault();
    if (!editable || isRequestPending.current || !formData || !formData.title.trim()) return;
    isRequestPending.current = true;
    setIsSaving(true);
    setErrorMessage('');
    try {
      if (formData.type === 'milestone') await handleSaveMilestone();
      else await handleSaveTask();
      if (isMounted.current) {
        setFormData(null);
        setShowMilestones(true);
      }
    } catch (error) {
      if (isMounted.current) setErrorMessage(error.response?.data?.error || error.response?.data?.message || error.message || 'Unable to save. Please try again.');
    } finally {
      isRequestPending.current = false;
      if (isMounted.current) setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editable || isRequestPending.current || !itemToDelete) return;
    isRequestPending.current = true;
    setIsDeleting(true);
    setErrorMessage('');
    try {
      if (itemToDelete.type === 'milestone') {
        const response = await axios.delete(`${Cookies.get('apiEnv')}/milestone/${itemToDelete.id}`, { params: getMilestoneParams() });
        if (isMounted.current) ToastStore.showToast(response.data.message, 'success');
        await refreshMilestones();
      } else {
        const response = await axios.delete(`${Cookies.get('apiEnv')}/task/${itemToDelete.id}`, {
          data: { ...getMilestoneParams(), milestone_id: itemToDelete.milestoneId }
        });
        if (isMounted.current) ToastStore.showToast(response.data.message, 'success');
        await refreshMilestones();
      }
      if (isMounted.current) setItemToDelete(null);
    } catch (error) {
      if (isMounted.current) setErrorMessage(error.response?.data?.error || error.response?.data?.message || error.message || 'Unable to delete. Please try again.');
    } finally {
      isRequestPending.current = false;
      if (isMounted.current) setIsDeleting(false);
    }
  };

  const handleTaskCompletion = async (milestone, task, completed) => {
    if (!editable || isRequestPending.current) return;
    isRequestPending.current = true;
    setPendingAction(`complete-task-${task.id}`);
    setErrorMessage('');
    try {
      const response = await axios.put(`${Cookies.get('apiEnv')}/task/${task.id}`, {
        ...getMilestoneParams(), milestone_id: milestone.id,
        title: task.title, status: completed ? 1 : 0
      });
      if (!isMounted.current) return;
      ToastStore.showToast(response.data.message, 'success');
      await refreshMilestones();
    } catch (error) {
      if (isMounted.current) setErrorMessage(error.response?.data?.error || error.response?.data?.message || error.message || 'Unable to update task. Please try again.');
    } finally {
      isRequestPending.current = false;
      if (isMounted.current) setPendingAction(null);
    }
  };

  const renderActions = (milestone, task = null) => {
    if (!editable) return null;
    const item = task || milestone;
    const type = task ? 'task' : 'milestone';
    return <div className="milestone-actions goal-item-actions">
      <Tooltip content={`Edit ${type}`} placement="top" portalContainer={document.body}>
        <Button minimal disabled={isLoading} aria-label={`Edit ${type}: ${item.title}`} icon={<img src={EditIcon} alt="" />} onClick={() => task ? openTaskDrawer(milestone, task) : openMilestoneDrawer(milestone)} />
      </Tooltip>
      <Tooltip content={`Delete ${type}`} placement="top" portalContainer={document.body}>
        <Button minimal disabled={isLoading} aria-label={`Delete ${type}: ${item.title}`} icon={<img src={DeleteIcon} alt="" />} onClick={() => {
          if (isRequestPending.current) return;
          setErrorMessage('');
          setItemToDelete({ type, id: item.id, title: item.title, milestoneId: milestone.id });
        }} />
      </Tooltip>
    </div>;
  };

  return (
    <div className="milestone-list">
      <div className="milestone-controls">
        {editable && (
          <FilledButton
            disabled={isLoading}
            text="+ Add milestone"
            onClick={() => handleCreate()}
            style={{
              backgroundColor: '#ffffff',
              color: '#1870d5',
              border: '1px solid #1870d5',
              borderRadius: '999px',
              fontWeight: 500,
              padding: '10px 18px',
              boxShadow: 'none',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.backgroundColor = '#1870d5';
              event.currentTarget.style.color = '#ffffff';
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor = '#ffffff';
              event.currentTarget.style.color = '#1870d5';
            }}
          />
        )}
        {pendingAction === 'create-milestone' && <LoadingIndicator size={20} aria-label="Creating milestone" />}

        <Button
          minimal
          disabled={milestones.length === 0}
          className="milestone-toggle"
          icon={milestonesOpen ? 'chevron-down' : 'chevron-right'}
          aria-expanded={milestonesOpen}
          onClick={() => setShowMilestones(!showMilestones)}
          text={`${milestones.length} Milestones`}
        />
      </div>
      {errorMessage && !formData && !itemToDelete && <div role="alert" className="bp3-callout bp3-intent-danger">{errorMessage}</div>}
      <GoalCollapse isOpen={milestonesOpen}>
        {milestones.map(milestone => {
          const tasks = getTasks(milestone);
          const progress = milestone.progress != null
            ? Number(milestone.progress) || 0
            : (tasks.length ? Math.round(tasks.filter(isCompleted).length / tasks.length * 1000) / 10 : 0);
          const tasksOpen = tasks.length > 0 && openTasks[milestone.id] === true;
          return <div className="milestone-card" key={milestone.id}>
            <div className="milestone-heading">
              <span className="milestone-icon"><Icon icon="flag" iconSize={17} /></span>
              <span className="goal-title">{milestone.title}</span>
              {renderActions(milestone)}
            </div>
            <div className="milestone-body">
              <div className="milestone-progress">
                <img src={Progress} alt="Progress" width={20} height={20} />
                <GoalProgressRow goal={{ progress, metric_type: 0 }} barWidth="min(290px, 45%)" showMatrix={false} />
              </div>
              <div className="milestone-controls">
                {editable && (
                  <FilledButton
                    disabled={isLoading}
                    text="+ Add task"
                    onClick={() => handleCreate(milestone)}
                    style={{
                      backgroundColor: '#ffffff',
                      color: '#1870d5',
                      border: '1px solid #1870d5',
                      borderRadius: '999px',
                      fontWeight: 500,
                      padding: '10px 18px',
                      boxShadow: 'none',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(event) => {
                      event.currentTarget.style.backgroundColor = '#1870d5';
                      event.currentTarget.style.color = '#ffffff';
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.backgroundColor = '#ffffff';
                      event.currentTarget.style.color = '#1870d5';
                    }}
                  />
                )}
                {pendingAction === `create-task-${milestone.id}` && <LoadingIndicator size={20} aria-label="Creating task" />}
                <Button minimal disabled={tasks.length === 0} className="milestone-toggle" icon={tasksOpen ? 'chevron-down' : 'chevron-right'} aria-expanded={tasksOpen} onClick={() => setOpenTasks(previous => ({ ...previous, [milestone.id]: !tasksOpen }))} text={`${tasks.length} Tasks`} />
              </div>
              <GoalCollapse isOpen={tasksOpen}>
                {tasks.map(task => <div className="milestone-task" key={task.id}>
                  <Checkbox checked={isCompleted(task)} disabled={!editable || isLoading} onChange={event => handleTaskCompletion(milestone, task, event.target.checked)}>
                    <span className={isCompleted(task) ? 'milestone-task-completed' : ''}>{task.title}</span>
                  </Checkbox>
                  {pendingAction === `complete-task-${task.id}` && <LoadingIndicator size={20} aria-label="Updating task" />}
                  {renderActions(milestone, task)}
                </div>)}
              </GoalCollapse>
            </div>
          </div>
        })}
      </GoalCollapse>

      {/* Drawer for updating milestones and tasks */}
      <Drawer
        portalContainer={document.body}
        isOpen={!!formData}
        onClose={closeDrawer}
        canEscapeKeyClose={!isLoading}
        canOutsideClickClose={!isLoading}
        size="40%"
        className="milestone-edit-drawer"
        title={formData ? `Update ${formData.type === 'milestone' ? 'Milestone' : 'Task'}` : ''}
      >
        {formData && <form onSubmit={handleSave} className="milestone-editor">
          <FormGroup label="Title" labelFor={`milestone-title-${subGoal.id}`} className="edit-label push-10-b push-10-t">
            <input id={`milestone-title-${subGoal.id}`} name="title" className="bp3-input bp3-control-no-margin push-10-b" placeholder="Title" autoFocus required value={formData.title} disabled={!editable || isLoading} onChange={event => setFormData({ ...formData, title: event.target.value })} />
          </FormGroup>
          {canEditMilestoneProgress && <FormGroup label="Progress" className="edit-label" style={{ marginTop: '20px' }}>
            <Slider
              labelStepSize={10}
              min={0}
              max={100}
              value={formData.progress}
              disabled={!editable || isLoading}
              onChange={progress => setFormData({ ...formData, progress })}
            />
          </FormGroup>}
          {/* {errorMessage && <div role="alert" className="bp3-callout bp3-intent-danger">{errorMessage}</div>} */}
          <div className="milestone-form-actions">
            <FilledButton type="submit" text={isSaving ? 'Saving...' : 'Save'} disabled={!editable || isLoading || !formData.title.trim()} />
            <TextButton type="button" text="Cancel" disabled={isLoading} onClick={closeDrawer} />
            {isSaving && <img src="https://s01.live2support.com/dashboardv2/chatassets/img/loader_new.gif" width={35} height={35} alt="Saving..." role="status" />}
          </div>
        </form>}
      </Drawer>

      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        isOpen={!!itemToDelete}
        onClose={closeDeleteDialog}
        onConfirm={handleDelete}
        title="Delete Confirmation"
        message={`Are you sure you want to delete this ${itemToDelete ? itemToDelete.type : 'item'} `}
        itemName={itemToDelete ? itemToDelete.title : ''}
        confirmText="Delete"
        confirmIntent="danger"
        confirmDisabled={!editable}
        isSubmitting={isLoading}
        submittingText="Deleting..."
        errorMessage={errorMessage}
        loadingIndicator={<img src="https://s01.live2support.com/dashboardv2/chatassets/img/loader_new.gif" width={35} height={35} alt="Deleting..." role="status" />}
      />
    </div>
  );
}
